<template>
  <div :id="id" class="chapter-section panel">
    <p class="panel-heading">{{ $t(`chapter1.title`) }}</p>

    <fieldset class="box">
      <legend>{{ $t(`chapter1.q0.title`) }}</legend>
      <b-field
        :label="$t(`chapter1.q0.subtitle`)"
        :type="store.getFieldError('studienparameter', 'eigeninitiiert') ? 'is-danger' : ''"
        :message="store.getFieldError('studienparameter', 'eigeninitiiert')"
      >
        <div class="is-flex is-flex-direction-column">
          <b-radio
            :required="true"
            v-model="study.studienparameter.eigeninitiiert"
            :native-value="true"
            @change="save"
          >
            {{ $t(`chapter1.q0.ja`) }}
          </b-radio>
          <b-radio
            :required="true"
            v-model="study.studienparameter.eigeninitiiert"
            :native-value="false"
            @change="save"
          >
            {{ $t(`chapter1.q0.nein`) }}
          </b-radio>
        </div>
      </b-field>

      <!-- Art der Datenerfassung -->
      <b-field
        :label="$t(`chapter1.q1.title`)"
        :type="
          store.getFieldError('studienparameter', 'studientypContainer.studientyp')
            ? 'is-danger'
            : ''
        "
        :message="store.getFieldError('studienparameter', 'studientypContainer.studientyp')"
      >
        <div v-for="type in studientypOptions" :key="type">
          <b-checkbox
            :required="true"
            v-model="study.studienparameter.studientypContainer.studientyp"
            :native-value="type"
            @change="onProspektivChange"
          >
            {{ $t(`chapter1.q1.${type}`) }}
          </b-checkbox>
        </div>
      </b-field>

      <b-field
        :label="$t(`chapter1.q2.title`)"
        :type="
          store.getFieldError('studienparameter', 'studientypContainer.studienzentren')
            ? 'is-danger'
            : ''
        "
        :message="store.getFieldError('studienparameter', 'studientypContainer.studienzentren')"
      >
        <div v-for="type in studienzentrenOptions" :key="type">
          <b-radio
            :required="true"
            v-model="study.studienparameter.studientypContainer.studienzentren"
            :native-value="type"
            @change="onStudienzentrenChange"
          >
            {{ $t(`chapter1.q2.${type}`) }}
          </b-radio>
        </div>
      </b-field>
      <b-field :label="$t(`chapter1.q3.title`)">
        <b-checkbox
          v-model="study.studienparameter.studientypContainer.registerstudie"
          type="is-success"
          @change="onRegisterstudieChange"
        >
          {{ $t(`chapter1.q3.text`) }}
        </b-checkbox>
      </b-field>

      <!-- Register sub-questions, outside the b-field above -->
      <div
        v-if="showRegisterVerwendung"
        class="box"
        style="margin-left: 0.5rem; margin-bottom: 0.5rem"
      >
        <b-field
          :label="$t(`chapter1.q3a.title`)"
          :type="
            store.getFieldError('studienparameter', 'studientypContainer.registerVerwendung')
              ? 'is-danger'
              : ''
          "
          :message="
            store.getFieldError('studienparameter', 'studientypContainer.registerVerwendung')
          "
        >
          <div v-for="option in registerVerwendungOptions" :key="option">
            <b-radio
              :required="showRegisterVerwendung"
              v-model="study.studienparameter.studientypContainer.registerVerwendung"
              :native-value="option"
              @change="save"
            >
              {{ $t(`chapter1.q3a.${option}`) }}
            </b-radio>
          </div>
        </b-field>
        <AbweichungStandardprozess v-if="showEigenesAufsetzen">
          {{ $t('general.abweichungStandardprozess') }}
        </AbweichungStandardprozess>

        <!-- eigenesAufsetzen sub-question -->
        <div v-if="showEigenesAufsetzen" class="box">
          <p>{{ $t(`chapter1.q3a1.title`) }}</p>
          <div class="is-flex is-flex-direction-column osse-radio-group">
            <b-radio
              v-model="study.studienparameter.studientypContainer.eigenesRegisterIstOsse"
              :native-value="true"
              @change="save"
            >
              {{ $t(`chapter1.q3a1.osseTrue`) }}
            </b-radio>
            <b-radio
              v-model="study.studienparameter.studientypContainer.eigenesRegisterIstOsse"
              :native-value="false"
              @change="save"
            >
              {{ $t(`chapter1.q3a1.osseFalse`) }}
            </b-radio>
          </div>
          <InfoToggleNotification :toggle="false">
            <p>{{ $t('chapter1.q3a.eigenesAufsetzenInfo') }}</p>
            <p v-if="study.studienparameter.studientypContainer.eigenesRegisterIstOsse">
              {{ $t('chapter1.q3a1.osseTrueInfo') }}
              <strong
                >{{ $t('chapter1.q3a1.osseTrueInfoBold') }}
                <a href="https://www.osse-register.de/downloads/" target="_blank" rel="noopener">
                  <b-icon icon="open-in-new" size="is-small" />
                  https://www.osse-register.de/downloads/
                </a>
              </strong>
            </p>
            <p v-else>
              {{ $t('chapter1.q3a1.osseFalseInfo') }}
            </p>
          </InfoToggleNotification>
        </div>

        <!-- bestehendesBefuellen sub-question -->
        <!-- The watcher on registerVerwendung below always sets bestehendesRegister before
             showBestehendesBefuellen can become true, so the non-null assertions here hold. -->
        <fieldset v-if="showBestehendesBefuellen" class="box">
          <b-field
            :label="$t(`chapter1.q3a2.title`)"
            :type="
              store.getFieldError('studienparameter', 'studientypContainer.bestehendesRegister.typ')
                ? 'is-danger'
                : ''
            "
            :message="
              store.getFieldError('studienparameter', 'studientypContainer.bestehendesRegister.typ')
            "
          >
            <div class="is-flex is-flex-direction-column">
              <b-radio
                :required="showBestehendesBefuellen"
                v-model="study.studienparameter.studientypContainer.bestehendesRegister!.typ"
                native-value="lokal"
                @change="save"
              >
                {{ $t(`chapter1.q3a2.lokal`) }}
              </b-radio>
              <b-radio
                :required="showBestehendesBefuellen"
                v-model="study.studienparameter.studientypContainer.bestehendesRegister!.typ"
                native-value="extern"
                @change="save"
              >
                {{ $t(`chapter1.q3a2.extern`) }}
              </b-radio>
            </div>
          </b-field>
          <b-field
            :label="$t(`chapter1.q3a2.name`)"
            :type="
              store.getFieldError(
                'studienparameter',
                'studientypContainer.bestehendesRegister.name',
              )
                ? 'is-danger'
                : ''
            "
            :message="
              store.getFieldError(
                'studienparameter',
                'studientypContainer.bestehendesRegister.name',
              )
            "
          >
            <b-input
              :required="showBestehendesBefuellen"
              v-model="study.studienparameter.studientypContainer.bestehendesRegister!.name"
              @blur="save"
            />
          </b-field>
          <b-field :label="$t(`chapter1.q3a2.link`)">
            <b-input
              v-model="study.studienparameter.studientypContainer.bestehendesRegister!.link"
              @blur="save"
            />
          </b-field>
        </fieldset>
      </div>
      <b-field
        :label="$t(`chapter1.q4.text`)"
        :type="store.getFieldError('studienparameter', 'studienaufbau') ? 'is-danger' : ''"
        :message="store.getFieldError('studienparameter', 'studienaufbau')"
      >
        <div v-for="option in studienaufbauOptions" :key="option">
          <b-radio
            :required="true"
            v-model="study.studienparameter.studienaufbau"
            :native-value="option"
            @change="onStudienaufbauChange"
          >
            {{ $t(`chapter1.q4.${option}`) }}
          </b-radio>
        </div>
      </b-field>
    </fieldset>

    <!-- Einwilligung -->
    <fieldset class="box">
      <legend>{{ $t(`chapter1.q5.title`) }}</legend>
      <b-field class="is-flex is-justify-content-flex-start">
        <b-checkbox
          v-model="study.studienparameter.einwilligung.einwilligungLiegtVor"
          type="is-success"
          @change="onEinwilligungChange"
        >
          {{ $t(`chapter1.q5.text`) }}
        </b-checkbox>
      </b-field>

      <div v-if="study.studienparameter.einwilligung.einwilligungLiegtVor">
        <b-field
          :label="$t(`chapter1.q5.text2`)"
          :type="
            store.getFieldError('studienparameter', 'einwilligung.einwilligungsTyp')
              ? 'is-danger'
              : ''
          "
          :message="store.getFieldError('studienparameter', 'einwilligung.einwilligungsTyp')"
        >
          <div v-for="option in einwilligungsTypOptions" :key="option">
            <b-radio
              v-model="study.studienparameter.einwilligung.einwilligungsTyp"
              :native-value="option"
              @change="save"
            >
              {{ $t(`chapter1.q5.${option}`) }}
            </b-radio>
          </div>
        </b-field>
        <p v-if="study.studienparameter.einwilligung.einwilligungsTyp === 'eigene'">
          <InfoToggleNotification :toggle="false">
            {{ $t('chapter1.q5.eigeneInfo') }}
          </InfoToggleNotification>
        </p>
      </div>

      <b-field
        v-if="getReasonForMissingConsent"
        :type="
          store.getFieldError('studienparameter', 'einwilligung.begruendungKeineEinwilligung')
            ? 'is-danger'
            : ''
        "
        :message="
          store.getFieldError('studienparameter', 'einwilligung.begruendungKeineEinwilligung')
        "
      >
        <b-input
          type="textarea"
          v-model="study.studienparameter.einwilligung.begruendungKeineEinwilligung"
          :placeholder="$t(`chapter1.q5.keineEinwilligungGrund`)"
          @blur="save"
        />
      </b-field>
    </fieldset>

    <!-- Ethikvotum -->
    <fieldset class="box">
      <legend>{{ $t(`chapter1.q6.title`) }}</legend>
      <b-field
        :type="
          store.getFieldError('studienparameter', 'ethikVotum.ethikVotumStatus') ? 'is-danger' : ''
        "
        :message="store.getFieldError('studienparameter', 'ethikVotum.ethikVotumStatus')"
      >
        <div v-for="option in ethikVotumStatusOptions" :key="option">
          <b-radio
            :required="true"
            v-model="study.studienparameter.ethikVotum.ethikVotumStatus"
            :native-value="option"
            @change="onEthikvotumChange"
          >
            {{ $t(`chapter1.q6.${option}`) }}
          </b-radio>
        </div>
      </b-field>
      <div v-if="study.studienparameter.ethikVotum.ethikVotumStatus === 'liegtVor'">
        <b-field
          :label="$t(`chapter1.q6.nrText`)"
          :type="
            store.getFieldError('studienparameter', 'ethikVotum.ethikVotumNummer')
              ? 'is-danger'
              : ''
          "
          :message="store.getFieldError('studienparameter', 'ethikVotum.ethikVotumNummer')"
        >
          <b-input
            :required="study.studienparameter.ethikVotum.ethikVotumStatus === 'liegtVor'"
            v-model="study.studienparameter.ethikVotum.ethikVotumNummer"
            @blur="save"
          />
        </b-field>
      </div>
      <div style="margin-top: 1rem">
        <InfoToggleNotification
          v-if="study.studienparameter.ethikVotum.ethikVotumStatus === 'liegtVor'"
          :toggle="false"
        >
          {{ $t('chapter1.q6.jaInfo') }}
        </InfoToggleNotification>
        <InfoToggleNotification
          v-if="study.studienparameter.ethikVotum.ethikVotumStatus === 'inBearbeitung'"
          :toggle="false"
        >
          {{ $t('chapter1.q6.inBearbeitungInfo') }}
        </InfoToggleNotification>
      </div>
    </fieldset>

    <!-- Registrierung -->
    <fieldset class="box">
      <legend>{{ $t('chapter1.q7.title') }}</legend>

      <div v-for="option in registrierungOptions" :key="option">
        <b-field class="is-flex is-justify-content-flex-start">
          <b-checkbox
            v-model="study.studienparameter.registrierung"
            :native-value="option"
            @change="onRegistrierungChange"
          >
            {{ $t(`chapter1.q7.${option}`) }}
          </b-checkbox>
        </b-field>
      </div>

      <div v-if="study.studienparameter.registrierung.includes('sonstige')" class="box">
        <b-field>
          <b-input
            expanded
            v-model="study.studienparameter.registrierungSonstige"
            :placeholder="$t(`chapter1.q7.sonstigePlaceholder`)"
            @blur="save"
          />
        </b-field>
      </div>
    </fieldset>

    <!-- Forschungszweck -->
    <div class="box">
      <b-field
        :label="$t(`chapter1.q8.title`)"
        :type="store.getFieldError('studienparameter', 'forschungszweck') ? 'is-danger' : ''"
        :message="store.getFieldError('studienparameter', 'forschungszweck')"
      >
        <b-input
          :required="true"
          type="textarea"
          v-model="study.studienparameter.forschungszweck"
          :placeholder="$t(`chapter1.q8.placeholder`)"
          @blur="save"
        />
      </b-field>
    </div>

    <!-- Rechtfertigung -->
    <fieldset class="box">
      <legend>
        {{ $t(`chapter1.q9.title`) }}
        <b-tooltip :label="$t(`general.showRechtstext`)" position="is-right" type="is-warning">
          <b-icon
            type="is-warning"
            icon="format-section"
            class="legal-toggle"
            :class="{ 'is-active': showLegal_c1q9 }"
            @click.native="showLegal_c1q9 = !showLegal_c1q9"
          />
        </b-tooltip>
      </legend>
      <RechtsText v-if="c1q9Key">
        {{ $t(c1q9Key) }}
      </RechtsText>
      <b-checkbox
        v-model="study.studienparameter.idatRechtfertigung"
        @change="onWeitereBegruendungenChange"
      >
        {{ $t(`chapter1.q9.checkboxText`) }}
      </b-checkbox>
      <b-field v-if="study.studienparameter.idatRechtfertigung">
        <b-input
          v-model="study.studienparameter.idatRechtfertigungWeitere"
          :placeholder="$t(`chapter1.q9.placeholder`)"
          @blur="save"
        />
      </b-field>
    </fieldset>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useStudyStore } from '@/stores/studyStore'
import { useRechtstextToggle } from '@/composables/useVisibilityToggles'
import InfoText from '@/components/InfoText.vue'
import RechtsText from '@/components/RechtsText.vue'
import { BCheckbox, BField, BIcon, BInput, BRadio, BTooltip } from 'buefy'
import AbweichungStandardprozess from '@/components/AbweichungStandardprozess.vue'

const InfoToggleNotification = InfoText

defineProps<{ id: string; modelValue?: object }>()

const store = useStudyStore()
const study = store.study
const title = 'Studienparameter'

const studienzentrenOptions = ['monozentrisch', 'multizentrisch']
const studientypOptions = ['retrospektiv', 'prospektiv']
const registerVerwendungOptions = ['eigenesAufsetzen', 'bestehendesBefuellen', 'bestehendesNutzen']
const einwilligungsTypOptions = ['broadConsent', 'eigene']
const ethikVotumStatusOptions = ['liegtVor', 'CTR', 'inBearbeitung']
const registrierungOptions = ['orbis', 'centraxx', 'sonstige']
const studienaufbauOptions = ['mdrIvdrMpdg', 'ctrAmg', 'berufsordnung']

const save = () => {
  store.updateChapter('studienparameter', study.studienparameter)
}

const showRegisterVerwendung = computed(
  () => study.studienparameter.studientypContainer.registerstudie === true,
)

watch(
  () => study.studienparameter.studientypContainer.registerVerwendung,
  (newVal) => {
    if (newVal === 'bestehendesBefuellen') {
      if (!study.studienparameter.studientypContainer.bestehendesRegister) {
        study.studienparameter.studientypContainer.bestehendesRegister = {
          link: '',
          name: '',
          typ: 'lokal',
        }
      }
    } else {
      study.studienparameter.studientypContainer.bestehendesRegister = undefined
    }
  },
)

const showEigenesAufsetzen = computed(
  () =>
    study.studienparameter.studientypContainer.registerstudie === true &&
    study.studienparameter.studientypContainer.registerVerwendung === 'eigenesAufsetzen',
)

const showBestehendesBefuellen = computed(
  () =>
    study.studienparameter.studientypContainer.registerstudie === true &&
    study.studienparameter.studientypContainer.registerVerwendung === 'bestehendesBefuellen',
)

const getReasonForMissingConsent = computed(
  () =>
    study.studienparameter.studientypContainer.studientyp.includes('prospektiv') &&
    !study.studienparameter.einwilligung.einwilligungLiegtVor,
)

const showLegal_c1q9 = useRechtstextToggle()

const c1q9Key = computed(() => {
  if (!showLegal_c1q9.value) return null

  const studientyp = study.studienparameter.studientypContainer.studientyp
  if (!Array.isArray(studientyp)) return null

  if (studientyp.length === 1 && studientyp[0] === 'retrospektiv') return 'rechtstexte.c1q9a'
  if (studientyp.length === 1 && studientyp[0] === 'prospektiv') return 'rechtstexte.c1q9b'
  if (
    studientyp.length === 2 &&
    ['retrospektiv', 'prospektiv'].every((v) =>
      studientyp.includes(v as 'retrospektiv' | 'prospektiv'),
    )
  )
    return 'rechtstexte.c1q9c'

  return null
})

function onProspektivChange() {
  if (!(study.studienparameter.studientypContainer.studientyp || []).includes('prospektiv')) {
    study.studienparameter.einwilligung.begruendungKeineEinwilligung = undefined
    study.datenherkunft.quellSystemeSonstige = undefined
    study.datenherkunft.quellSysteme = []
    study.datenherkunft.verwendeteGeraete.verwendeteGeraeteSonstiges = undefined
    study.datenherkunft.verwendeteGeraete.mobilgeraeteHerkunft = []
    study.datenherkunft.verwendeteGeraete.geraete = []
  }
  save()
}

function onStudienzentrenChange() {
  if (!showRegisterVerwendung.value) {
    study.studienparameter.studientypContainer.registerVerwendung = undefined
  }
  save()
}

function onRegisterstudieChange() {
  save()
}

function onStudienaufbauChange() {
  if (study.studienparameter.studienaufbau === 'ctrAmg') {
    study.studienparameter.ethikVotum.ethikVotumStatus = 'CTR'
  }
  save()
}

function onEinwilligungChange() {
  if (!study.studienparameter.einwilligung.einwilligungLiegtVor) {
    study.studienparameter.einwilligung.einwilligungsTyp = undefined
    study.studienparameter.einwilligung.begruendungKeineEinwilligung = undefined
  }
  save()
}

function onEthikvotumChange() {
  if (study.studienparameter.ethikVotum.ethikVotumStatus !== 'liegtVor') {
    study.studienparameter.ethikVotum.ethikVotumNummer = undefined
  }
  save()
}

function onRegistrierungChange() {
  if (!(study.studienparameter.registrierung || []).includes('sonstige')) {
    study.studienparameter.registrierungSonstige = ''
  }
  save()
}

function onWeitereBegruendungenChange() {
  if (!study.studienparameter.idatRechtfertigung) {
    study.studienparameter.idatRechtfertigungWeitere = ''
  }
  save()
}
</script>

<style scoped>
.osse-radio-group {
  margin-bottom: var(--bulma-block-spacing);
}
</style>
